import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Save, PlugZap, CheckCircle2, XCircle, MinusCircle, Info } from 'lucide-react';
import api from '../../../services/api';
import { useI18n } from '../../../i18n/I18nProvider';
import { errorText } from '../../../lib/errors';
import { Card, CardHeader, CardBody, Field, Input, Button, StatusBadge, Switch } from '../../../ui';
import { useCompanyStats, posHealthFromStats } from './OverviewTab';
import { ServioStatusTiles } from '../../../features/servio/ServioStatus';

const POS_FIELDS = ['servio_url', 'servio_term_id', 'servio_code', 'pos_code'];
const SYSTEM_FIELDS = ['servio_url_sys', 'system_term', 'system_card'];
const SECRET_FIELDS = new Set(['servio_code', 'system_card']);

export default function PosTab({ tenant, onSaved }) {
  const { t, fmt } = useI18n();
  const [stats, reloadStats] = useCompanyStats(tenant.id);
  const initial = useMemo(
    () => Object.fromEntries([...POS_FIELDS, ...SYSTEM_FIELDS].map((f) => [f, SECRET_FIELDS.has(f) ? '' : (tenant.pos_config?.[f] ?? '')])),
    [tenant]
  );
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState(null);

  useEffect(() => { setForm(initial); }, [initial]);

  const changed = Object.fromEntries(Object.entries(form).filter(([k, v]) => String(v).trim() !== String(initial[k]).trim()));
  const dirty = Object.keys(changed).length > 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/admin/tenants/${tenant.id}`, { pos_config: changed });
      toast.success(t('pos.saved'));
      onSaved();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const runCheck = async () => {
    setChecking(true);
    setCheck(null);
    try {
      const res = await api.post(`/admin/tenants/${tenant.id}/servio-check`);
      setCheck(res.data.data);
      reloadStats();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setChecking(false);
    }
  };

  const field = (key) => (
    <Field key={key} label={t(`pos.${key}`)}>
      <Input
        type={SECRET_FIELDS.has(key) ? 'password' : 'text'}
        autoComplete="off"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        placeholder={SECRET_FIELDS.has(key) ? (tenant.pos_secrets?.[key] ? t('pos.secretSaved') : t('pos.secretEmpty')) : ''}
        className="font-mono"
      />
    </Field>
  );

  const sync = stats?.pos?.sync;
  const isDemo = tenant.pos_type === 'demo';
  const [savingType, setSavingType] = useState(false);
  const setDemo = async (demo) => {
    setSavingType(true);
    try {
      await api.put(`/admin/tenants/${tenant.id}`, { pos_type: demo ? 'demo' : 'servio' });
      toast.success(t('pos.saved'));
      onSaved();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSavingType(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
      <form onSubmit={save} className="space-y-6">
        <Card>
          <CardBody className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('pos.demoTitle')}</p>
              <p className="mt-0.5 max-w-xl text-xs text-zinc-500">{isDemo ? t('pos.demoOnHint') : t('pos.demoOffHint')}</p>
            </div>
            <Switch checked={isDemo} onChange={setDemo} disabled={savingType || tenant.is_default} label={t('pos.demoTitle')} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t('pos.title')} description={t('pos.subtitle')} />
          <CardBody className="space-y-6">
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t('pos.posSection')}</p>
              <div className="grid gap-4 sm:grid-cols-2">{POS_FIELDS.map(field)}</div>
            </div>
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-zinc-400">{t('pos.systemSection')}</p>
              <div className="grid gap-4 sm:grid-cols-2">{SYSTEM_FIELDS.map(field)}</div>
            </div>
            {tenant.is_default && (
              <p className="flex items-start gap-2 rounded-lg bg-zinc-50 px-3 py-2 text-xs text-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-400">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t('pos.defaultHint')}
              </p>
            )}
          </CardBody>
        </Card>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" disabled={!dirty || saving} onClick={() => setForm(initial)}>{t('common.cancel')}</Button>
          <Button type="submit" icon={Save} loading={saving} disabled={!dirty}>{t('common.save')}</Button>
        </div>
      </form>

      <div className="space-y-6">
        <Card>
          <CardHeader title={t('company.posCard')} />
          <CardBody className="space-y-4">
            {stats && <StatusBadge kind="pos" value={posHealthFromStats(stats.pos)} />}
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500 dark:text-zinc-400">{t('company.lastSync')}</dt>
                <dd className="tabular text-right text-zinc-900 dark:text-zinc-100">{sync?.last_run_at ? fmt.relative(sync.last_run_at) : t('common.never')}</dd>
              </div>
              {sync?.last_run_at && (
                <p className="text-right text-xs text-zinc-500">{t('company.syncedCount', { synced: sync.last_synced ?? 0, failed: sync.last_failed ?? 0 })}</p>
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500 dark:text-zinc-400">{t('company.txSync')}</dt>
                <dd className="tabular text-right text-zinc-900 dark:text-zinc-100">{sync?.tx_last_run_at ? fmt.relative(sync.tx_last_run_at) : t('common.never')}</dd>
              </div>
              {sync?.tx_last_run_at && (
                <p className={`text-right text-xs ${sync.tx_last_failed ? 'text-amber-700 dark:text-amber-400' : 'text-zinc-500'}`} title={sync.tx_last_error || undefined}>
                  {t('company.txSyncCount', { cards: sync.tx_last_cards ?? 0, inserted: sync.tx_last_new ?? 0, failed: sync.tx_last_failed ?? 0 })}
                </p>
              )}
              <div className="flex justify-between gap-3">
                <dt className="text-zinc-500 dark:text-zinc-400">{t('company.lastCheck')}</dt>
                <dd className="tabular text-right text-zinc-900 dark:text-zinc-100">{sync?.last_check_at ? fmt.relative(sync.last_check_at) : t('common.never')}</dd>
              </div>
            </dl>
            <Button variant="secondary" icon={PlugZap} onClick={runCheck} loading={checking} disabled={dirty} className="w-full justify-center">
              {checking ? t('pos.checking') : t('pos.check')}
            </Button>
            {check && (
              <ul className="space-y-2">
                <CheckRow label={t('pos.checkPos')} result={check.pos} />
                <CheckRow label={t('pos.checkSystem')} result={check.system} />
              </ul>
            )}
          </CardBody>
        </Card>
        {stats?.pos.configured && (
          <Card>
            <CardHeader title={t('servio.title')} description={t('servio.subtitle')} />
            <CardBody>
              <div className="[&>div]:grid-cols-1"><ServioStatusTiles sync={sync} /></div>
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
}

function CheckRow({ label, result }) {
  const { t } = useI18n();
  const Icon = result.skipped ? MinusCircle : result.ok ? CheckCircle2 : XCircle;
  const tone = result.skipped ? 'text-zinc-400' : result.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400';
  return (
    <li className="rounded-lg border border-zinc-200 px-3 py-2 dark:border-zinc-700">
      <p className="flex items-center gap-2 text-sm font-medium text-zinc-900 dark:text-zinc-100">
        <Icon className={`h-4 w-4 ${tone}`} />
        {label}
      </p>
      <p className="mt-0.5 break-words text-xs text-zinc-500 dark:text-zinc-400">
        {result.skipped ? t('pos.checkSkipped') : result.ok ? t('pos.checkOk', { ms: result.ms }) : result.error}
      </p>
    </li>
  );
}
